import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, DollarSign, Trophy, ArrowRight, TrendingUp, TrendingDown, Target,
  CalendarClock, Video, Activity as ActivityIcon, BarChart3, UserPlus, MessageSquare, Star,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi, useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import { formatDate, formatTime } from '@/lib/dateFormat';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { TrendChart, Donut } from '@/components/dashboard/DashboardCharts';

// Super-admin dashboard — same layout as the vendor dashboard, with the
// platform (orgs / revenue / health / NPS) and sales (prospects / demos) views.

interface Stats {
  totalOrgs: number; activeOrgs: number; totalLeads: number; mrr: number;
  activationRate?: number; activatedOrgs?: number;
  weeklyActiveOrgs?: number; weeklyActiveRate?: number;
  conversionRate?: number; paidOrgs?: number;
  churnedOrgs?: number;
}
interface Feedback {
  summary: { nps: number | null; total: number; promoters: number; passives: number; detractors: number; avgScore: number | null };
  responses: { id: string; tenantName: string | null; score: number; comment: string | null; createdAt: string }[];
}
interface Analytics {
  mrr: number; arr: number; activeSubscriptions: number; cancelled30d: number; churnRate: number;
  signupsLast30d: number;
  signupsTrend: { date: string; count: number }[];
  planDistribution: { planId: string; planName: string; price: number; count: number; revenue: number }[];
  topTenants: { tenantId: string; name: string; leadCount: number }[];
}
interface Org { id: string; name: string; ownerEmail: string; plan: string; status: string; joinedAt: string }
interface Sales {
  kpis: { totalProspects: number; newThisMonth: number; won: number; conversionRate: number; upcomingDemos: number; openPipelineValue: number };
  monthlyTrend: { month: string; count: number }[];
  pipeline: { status: string; count: number }[];
  topSources: { name: string; count: number }[];
  demosToday: { id: string; guestName: string; startAt: string; endAt: string; meetingLink: string | null; prospectId: string | null }[];
  recentProspects: { id: string; companyName: string; contactName: string; status: string; source: string | null; createdAt: string; assignedTo: string | null }[];
  recentActivity: { id: string; actionType: string; payload: any; actorName: string | null; createdAt: string; prospect: { id: string; companyName: string; source: string | null } | null }[];
  topPerformers: { id: string; name: string; won: number; prospects: number }[];
}

// Prospect pipeline stages (ids from the backend) — label + chart color.
const STAGES: Record<string, { label: string; color: string }> = {
  new:            { label: 'New',            color: '#3B82F6' },
  contacted:      { label: 'Contacted',      color: '#F59E0B' },
  demo_scheduled: { label: 'Demo Scheduled', color: '#8B5CF6' },
  demo_done:      { label: 'Demo Done',      color: '#6366F1' },
  proposal_sent:  { label: 'Proposal Sent',  color: '#A855F7' },
  negotiating:    { label: 'Negotiating',    color: '#F97316' },
  won:            { label: 'Won',            color: '#10B981' },
  lost:           { label: 'Lost',           color: '#94A3B8' },
};
const stageLabel = (s: string) => STAGES[s]?.label ?? s;

const ORG_STATUS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  suspended: 'bg-red-50 text-red-700',
};

const nf = (n: number) => n.toLocaleString('en-IN');
const usd = (n: number) => `$${n.toLocaleString()}`;

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d < 7 ? `${d} day${d > 1 ? 's' : ''} ago` : formatDate(iso);
}
const fmtTime = (iso: string) => formatTime(iso);
function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

// Prospect timeline entry → short title + detail for the activity feed.
function activityText(a: Sales['recentActivity'][number]): { title: string; detail: string } {
  const p = a.payload ?? {};
  switch (a.actionType) {
    case 'status_changed': return { title: 'Status changed', detail: `${stageLabel(p.from)} → ${stageLabel(p.to)}${p.note ? ` — ${p.note}` : ''}` };
    case 'note_added': return { title: 'Note', detail: p.body ?? '' };
    case 'call_made': return { title: 'Call logged', detail: p.summary ?? '' };
    case 'email_sent': return { title: 'Email logged', detail: p.summary ?? '' };
    case 'whatsapp_sent': return { title: 'WhatsApp logged', detail: p.summary ?? '' };
    case 'created': return { title: 'Prospect created', detail: p.source ? `from ${String(p.source).replace(/_/g, ' ')}` : '' };
    case 'converted_to_tenant': return { title: 'Converted to tenant', detail: [p.tenantName, p.planName].filter(Boolean).join(' · ') };
    default: return { title: a.actionType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), detail: p.summary ?? p.note ?? '' };
  }
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}
function CardHead({ icon, title, subtitle, href }: { icon?: React.ReactNode; title: string; subtitle?: string; href?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-start gap-2.5 min-w-0">
        {icon && <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-[#6366F1]">{icon}</div>}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {href && (
        <Link to={href} className="inline-flex items-center gap-1 text-xs text-[#6366F1] hover:underline flex-shrink-0">
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}
const Empty = ({ text }: { text: string }) => <p className="py-8 text-center text-sm text-slate-400">{text}</p>;
const Skeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="space-y-2">{Array.from({ length: rows }).map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded-lg animate-pulse" />)}</div>
);

export function SuperAdminDashboardPage() {
  const { admin } = useSuperAdminAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [sales, setSales] = useState<Sales | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const { sorted: sortedOrgs, sort, onSort } = useSort(orgs);

  useEffect(() => {
    Promise.all([
      saApi.get('/super-admin/stats').catch(() => ({ data: null })),
      saApi.get('/super-admin/organizations').catch(() => ({ data: [] })),
      saApi.get('/super-admin/analytics').catch(() => ({ data: null })),
      saApi.get('/super-admin/feedback').catch(() => ({ data: null })),
      saApi.get('/super-admin/prospects/dashboard').catch(() => ({ data: null })),
    ]).then(([s, o, a, f, sd]) => {
      setStats(s.data);
      setOrgs(Array.isArray(o.data) ? o.data.slice(0, 10) : []);
      setAnalytics(a.data);
      setFeedback(f.data);
      setSales(sd.data);
    }).finally(() => setLoading(false));
  }, []);

  const k = sales?.kpis;
  const pipeline = (sales?.pipeline ?? [])
    .map((p) => ({ name: stageLabel(p.status), color: STAGES[p.status]?.color ?? '#94A3B8', count: p.count }))
    .sort((a, b) => b.count - a.count);
  const pipelineTotal = pipeline.reduce((a, p) => a + p.count, 0);
  const monthly = sales?.monthlyTrend ?? [];
  const yearTotal = monthly.reduce((a, m) => a + m.count, 0);
  const sources = sales?.topSources ?? [];
  const sourceMax = Math.max(1, ...sources.map((s) => s.count));
  const demosToday = sales?.demosToday ?? [];
  const signupMax = Math.max(1, ...(analytics?.signupsTrend ?? []).map((p) => p.count));
  const firstName = (admin?.name || '').split(' ')[0] || 'there';
  const todayLabel = useMemo(() => formatDate(new Date().toISOString()), []);

  const kpiCards = [
    { label: 'Organizations', value: stats ? nf(stats.totalOrgs) : '—', sub: stats ? `${nf(stats.activeOrgs)} active subscriptions` : '', icon: <Building2 className="h-4 w-4" />, tone: 'bg-indigo-50 text-[#6366F1]', href: '/super-admin/organizations' },
    { label: 'MRR', value: stats ? usd(stats.mrr) : '—', sub: analytics ? `${usd(analytics.arr)} ARR` : '', icon: <DollarSign className="h-4 w-4" />, tone: 'bg-amber-50 text-amber-600', href: '/super-admin/revenue' },
    { label: 'Prospects', value: k ? nf(k.totalProspects) : '—', sub: k ? `${nf(k.newThisMonth)} new this month` : '', icon: <Target className="h-4 w-4" />, tone: 'bg-sky-50 text-sky-600', href: '/super-admin/prospects' },
    { label: 'Prospects won', value: k ? nf(k.won) : '—', sub: k ? `${k.conversionRate}% conversion · ${nf(k.upcomingDemos)} demos this week` : '', icon: <Trophy className="h-4 w-4" />, tone: 'bg-emerald-50 text-emerald-600', href: '/super-admin/prospects' },
  ];

  const healthCards = [
    { label: 'Activation', value: `${stats?.activationRate ?? 0}%`, sub: `${stats?.activatedOrgs ?? 0} orgs added a lead` },
    { label: 'Weekly active', value: nf(stats?.weeklyActiveOrgs ?? 0), sub: `${stats?.weeklyActiveRate ?? 0}% active in last 7 days` },
    { label: 'Free → Paid', value: `${stats?.conversionRate ?? 0}%`, sub: `${stats?.paidOrgs ?? 0} paying orgs` },
    { label: 'Leads on platform', value: nf(stats?.totalLeads ?? 0), sub: `${stats?.churnedOrgs ?? 0} orgs churned` },
  ];

  return (
    <SuperAdminLayout title="Dashboard">
      <div className="space-y-5">
        {/* Greeting + quick links */}
        <Card className="relative overflow-hidden p-5">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-indigo-50" />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs text-slate-500">{todayLabel} · BrixLead platform</p>
              <h2 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900">{greeting()}, {firstName}</h2>
              <p className="mt-1 text-sm text-slate-500">
                You have <b className="text-slate-800">{demosToday.length}</b> demos today ·{' '}
                <b className="text-slate-800">{k ? nf(k.newThisMonth) : 0}</b> new prospects and{' '}
                <b className="text-slate-800">{analytics ? nf(analytics.signupsLast30d) : 0}</b> signups in the last 30 days.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { to: '/super-admin/prospects', label: 'Prospects', icon: <Target className="h-4 w-4" /> },
                { to: '/super-admin/demos', label: 'Demos', icon: <CalendarClock className="h-4 w-4" /> },
                { to: '/super-admin/organizations', label: 'Organizations', icon: <Building2 className="h-4 w-4" /> },
                { to: '/super-admin/revenue', label: 'Revenue', icon: <BarChart3 className="h-4 w-4" /> },
              ].map((q) => (
                <Link key={q.to} to={q.to} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50">
                  {q.icon} {q.label}
                </Link>
              ))}
            </div>
          </div>
        </Card>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {kpiCards.map((c) => (
            <Link key={c.label} to={c.href} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <p className="text-sm text-slate-500">{c.label}</p>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.tone}`}>{c.icon}</span>
              </div>
              {loading ? <div className="mt-3 h-7 w-24 rounded bg-slate-100 animate-pulse" /> : <p className="mt-2 text-2xl font-bold text-slate-900">{c.value}</p>}
              <p className="mt-1 text-xs text-slate-500">{c.sub}</p>
            </Link>
          ))}
        </div>

        {/* Product health */}
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          {healthCards.map((c) => (
            <Card key={c.label} className="p-4">
              <p className="text-xs text-slate-500">{c.label}</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{loading ? '—' : c.value}</p>
              <p className="mt-0.5 text-xs text-slate-500">{c.sub}</p>
            </Card>
          ))}
        </div>

        {/* Prospect trend + pipeline */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Prospect trend</h3>
                <p className="text-xs text-slate-500">New prospects by month · last 12 months</p>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#6366F1]">
                <TrendingUp className="h-4 w-4" /> {nf(yearTotal)}
              </span>
            </div>
            {loading ? <div className="h-48 rounded-lg bg-slate-50 animate-pulse" /> : monthly.length ? <TrendChart data={monthly} unit="prospects" label="New prospects by month" /> : <Empty text="No prospects yet" />}
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-900">Sales pipeline</h3>
            <p className="text-xs text-slate-500 mb-4">
              Prospects by stage{k?.openPipelineValue ? ` · ₹${nf(k.openPipelineValue)} open value` : ''}
            </p>
            {loading ? <div className="h-40 rounded-lg bg-slate-50 animate-pulse" /> : !pipeline.length ? <Empty text="No prospects yet" /> : (
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start lg:flex-col xl:flex-row">
                <Donut items={pipeline} total={pipelineTotal} unit="prospects" />
                <ul className="w-full min-w-0 space-y-1.5">
                  {pipeline.map((p) => (
                    <li key={p.name} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="flex-1 truncate text-slate-700">{p.name}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{nf(p.count)}</span>
                      <span className="w-9 text-right text-slate-400 tabular-nums">{pipelineTotal ? Math.round((p.count / pipelineTotal) * 100) : 0}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>

        {/* Demos today + signups + revenue */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5">
            <CardHead icon={<CalendarClock className="h-4 w-4" />} title="Demos today" subtitle="Your scheduled demos" href="/super-admin/demos" />
            {loading ? <Skeleton /> : !demosToday.length ? <Empty text="No demos today" /> : (
              <ul className="divide-y divide-slate-100">
                {demosToday.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-slate-800">{d.guestName}</p>
                      <p className="text-xs text-slate-500">{fmtTime(d.startAt)} – {fmtTime(d.endAt)}</p>
                    </div>
                    {d.meetingLink && (
                      <a href={d.meetingLink} target="_blank" rel="noopener noreferrer" className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700">
                        <Video className="h-3.5 w-3.5" /> Join
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Signups</h3>
                <p className="text-xs text-slate-500">New organizations · last 30 days</p>
              </div>
              <span className="text-sm font-semibold text-slate-900">{analytics ? nf(analytics.signupsLast30d) : '—'}</span>
            </div>
            {loading ? <div className="h-32 rounded-lg bg-slate-50 animate-pulse" /> : !analytics?.signupsTrend?.length ? <Empty text="No signups yet" /> : (
              <div className="flex items-end gap-[2px] h-32">
                {analytics.signupsTrend.map((p) => (
                  <div
                    key={p.date}
                    className="flex-1 rounded-t bg-[#6366F1]/80 hover:bg-[#6366F1] min-h-[3px] transition-colors"
                    style={{ height: `${(p.count / signupMax) * 100}%` }}
                    title={`${formatDate(p.date)}: ${p.count} signups`}
                  />
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<DollarSign className="h-4 w-4" />} title="Revenue & churn" href="/super-admin/revenue" />
            {loading ? <Skeleton rows={4} /> : !analytics ? <Empty text="No revenue data" /> : (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between"><span className="text-slate-500">ARR</span><span className="font-semibold text-slate-900">{usd(analytics.arr)}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-500">Active subscriptions</span><span className="font-semibold text-slate-900">{nf(analytics.activeSubscriptions)}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-500">Churn (30d)</span><span className="inline-flex items-center gap-1 font-semibold text-red-600"><TrendingDown className="h-4 w-4" /> {analytics.churnRate}%</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-500">Cancelled (30d)</span><span className="font-semibold text-slate-900">{nf(analytics.cancelled30d)}</span></div>
              </div>
            )}
          </Card>
        </div>

        {/* Recent prospects + top sources */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <CardHead icon={<UserPlus className="h-4 w-4" />} title="Recent prospects" subtitle="Newest first" href="/super-admin/prospects" />
            {loading ? <Skeleton rows={5} /> : !sales?.recentProspects?.length ? <Empty text="No prospects yet" /> : (
              <ul className="divide-y divide-slate-100">
                {sales.recentProspects.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 py-2.5">
                    <span className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-[#6366F1]">
                      {(p.companyName || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-800">{p.companyName}</span>
                      <span className="block truncate text-xs text-slate-500 capitalize">
                        {p.contactName} · {(p.source || 'unknown').replace(/_/g, ' ')}{p.assignedTo ? ` · ${p.assignedTo}` : ''}
                      </span>
                    </span>
                    <span
                      className="hidden sm:inline-flex rounded-full px-2 py-0.5 text-xs"
                      style={{ backgroundColor: `${STAGES[p.status]?.color ?? '#94A3B8'}1A`, color: STAGES[p.status]?.color ?? '#475569' }}
                    >
                      {stageLabel(p.status)}
                    </span>
                    <span className="w-20 text-right text-xs text-slate-400 flex-shrink-0">{timeAgo(p.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead title="Top sources" subtitle="All prospects" />
            {loading ? <Skeleton /> : !sources.length ? <Empty text="No prospects yet" /> : (
              <ul className="space-y-3">
                {sources.map((s) => (
                  <li key={s.name}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate text-slate-700 capitalize">{s.name.replace(/_/g, ' ')}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{nf(s.count)}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#6366F1]" style={{ width: `${(s.count / sourceMax) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Recent activity + top performers */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <CardHead icon={<ActivityIcon className="h-4 w-4" />} title="Recent activity" subtitle="Across all prospects" />
            {loading ? <Skeleton rows={5} /> : !sales?.recentActivity?.length ? <Empty text="No activity yet" /> : (
              <ul className="space-y-3">
                {sales.recentActivity.map((a) => {
                  const t = activityText(a);
                  return (
                    <li key={a.id} className="flex gap-3">
                      <span className="mt-0.5 h-7 w-7 flex-shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                        <ActivityIcon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800">
                          <span className="font-medium">{a.prospect?.companyName ?? 'Prospect'}</span>
                          <span className="text-slate-500"> · {t.title}</span>
                        </p>
                        {t.detail && <p className="text-sm text-slate-600 line-clamp-2 break-words">{t.detail}</p>}
                        <p className="text-xs text-slate-400">{a.actorName ?? 'System'} · {timeAgo(a.createdAt)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<Trophy className="h-4 w-4" />} title="Top performers" subtitle="Won prospects" />
            {loading ? <Skeleton rows={4} /> : !sales?.topPerformers?.length ? <Empty text="No won prospects yet" /> : (
              <ul className="space-y-3">
                {sales.topPerformers.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-3">
                    <span className={`h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-slate-800">{p.name}</p>
                      <p className="text-xs text-slate-500">{nf(p.prospects)} prospects · {p.prospects ? ((p.won / p.prospects) * 100).toFixed(1) : '0'}% conv.</p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-600 tabular-nums">{nf(p.won)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Vendor NPS + feedback */}
        {feedback && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-5">
              <CardHead icon={<Star className="h-4 w-4" />} title="Vendor NPS" href="/super-admin/feedback" />
              {feedback.summary.total === 0 ? <Empty text="No feedback yet" /> : (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-4xl font-bold ${(feedback.summary.nps ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{feedback.summary.nps ?? '—'}</span>
                    <span className="text-xs text-slate-400">NPS · {feedback.summary.total} responses</span>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-emerald-600">Promoters (9–10)</span><span className="font-semibold">{feedback.summary.promoters}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Passives (7–8)</span><span className="font-semibold">{feedback.summary.passives}</span></div>
                    <div className="flex justify-between"><span className="text-red-500">Detractors (0–6)</span><span className="font-semibold">{feedback.summary.detractors}</span></div>
                    <div className="flex justify-between pt-1 border-t border-slate-100"><span className="text-slate-500">Avg score</span><span className="font-semibold">{feedback.summary.avgScore ?? '—'}/10</span></div>
                  </div>
                </>
              )}
            </Card>
            <Card className="p-5 lg:col-span-2">
              <CardHead icon={<MessageSquare className="h-4 w-4" />} title="Recent feedback" subtitle="Written comments from vendors" />
              {feedback.responses.filter((r) => r.comment).length === 0 ? <Empty text="No written comments yet" /> : (
                <ul className="space-y-3 max-h-64 overflow-y-auto">
                  {feedback.responses.filter((r) => r.comment).slice(0, 12).map((r) => (
                    <li key={r.id} className="flex items-start gap-3 text-sm">
                      <span className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                        r.score >= 9 ? 'bg-emerald-50 text-emerald-700' : r.score >= 7 ? 'bg-slate-100 text-slate-600' : 'bg-red-50 text-red-600'
                      }`}>{r.score}</span>
                      <div className="min-w-0">
                        <p className="text-slate-700">{r.comment}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{r.tenantName || '—'} · {formatDate(r.createdAt)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {/* Plans + top tenants */}
        {analytics && (analytics.planDistribution.length > 0 || analytics.topTenants.length > 0) && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-5">
              <CardHead title="Plan distribution" subtitle="Organizations per plan" href="/super-admin/plans" />
              {!analytics.planDistribution.length ? <Empty text="No plans yet" /> : (
                <ul className="space-y-2">
                  {analytics.planDistribution.map((p) => (
                    <li key={p.planId || p.planName} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{p.planName}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-slate-500">{nf(p.count)} orgs</span>
                        <span className="font-semibold text-slate-900">{usd(p.revenue)}/mo</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card className="p-5 lg:col-span-2">
              <CardHead icon={<Users className="h-4 w-4" />} title="Top tenants" subtitle="By lead volume" href="/super-admin/organizations" />
              {!analytics.topTenants.length ? <Empty text="No tenants yet" /> : (
                <ul className="space-y-3">
                  {analytics.topTenants.map((t) => {
                    const max = Math.max(1, ...analytics.topTenants.map((x) => x.leadCount));
                    return (
                      <li key={t.tenantId}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate text-slate-700">{t.name}</span>
                          <span className="font-semibold text-slate-900 tabular-nums">{nf(t.leadCount)}</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-[#6366F1]" style={{ width: `${(t.leadCount / max) * 100}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        )}

        {/* Recent organizations */}
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Recent organizations</h3>
              <p className="text-xs text-slate-500">Latest signups</p>
            </div>
            <Link to="/super-admin/organizations" className="inline-flex items-center gap-1 text-xs text-[#6366F1] hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="Organization" sortKey="name" sort={sort} onSort={onSort} className="px-5 py-3" />
                  <SortTh label="Owner email" sortKey="ownerEmail" sort={sort} onSort={onSort} className="px-5 py-3" />
                  <SortTh label="Plan" sortKey="plan" sort={sort} onSort={onSort} className="px-5 py-3" />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} className="px-5 py-3" />
                  <SortTh label="Joined" sortKey="joinedAt" sort={sort} onSort={onSort} className="px-5 py-3" />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400 text-sm">Loading…</td></tr>
                ) : orgs.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400 text-sm">No organizations yet</td></tr>
                ) : sortedOrgs.map((org) => (
                  <tr key={org.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-slate-900">{org.name}</td>
                    <td className="px-5 py-3 text-slate-500">{org.ownerEmail}</td>
                    <td className="px-5 py-3 text-slate-500">{org.plan}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs capitalize ${ORG_STATUS[org.status] || ORG_STATUS.inactive}`}>{org.status}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{formatDate(org.joinedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
